/**
 * `<DemoChangePicker/>` - the list of changes a demo visitor can pick (#2824).
 *
 * One list: "The team as it is" and then each stored change on one line, with
 * its stored label. One button applies the pick. The same list sits in the
 * guide's "Pick a change" step and in the roster page's drawer.
 */
import { useId, useState } from "react";

import { Button } from "../ui";

export interface DemoChangeOption {
  id: string;
  label: string;
}

export interface DemoChangePickerProps {
  changes: DemoChangeOption[];
  /** The change the team shows now, or null for the team as it is. */
  current: string | null;
  busy: boolean;
  /** The refusal to show, if the last try failed. */
  error?: string | null;
  /** The pick: a change id, or null for the team as it is. */
  onApply: (variant: string | null) => void;
}

const AS_IT_IS = "The team as it is";

export function DemoChangePicker({
  changes,
  current,
  busy,
  error,
  onApply,
}: DemoChangePickerProps) {
  const name = useId();
  const [selected, setSelected] = useState<string | null>(current);
  const rows: DemoChangeOption[] = [{ id: "", label: AS_IT_IS }, ...changes];
  return (
    <div className="flex flex-col gap-3" data-testid="demo-change-picker">
      <fieldset className="flex flex-col gap-1.5" disabled={busy}>
        <legend className="sr-only">Available changes</legend>
        {rows.map((row) => {
          const value = row.id === "" ? null : row.id;
          return (
            <label
              key={row.id || "base"}
              className="flex min-h-tap cursor-pointer items-start gap-2 rounded-md border border-border px-3 py-2 text-sm has-[:checked]:border-accent has-[:checked]:bg-bg-subtle"
            >
              <input
                type="radio"
                name={name}
                checked={selected === value}
                onChange={() => setSelected(value)}
                className="mt-0.5"
                data-testid={`demo-change-option-${row.id || "base"}`}
              />
              <span className="text-fg">{row.label}</span>
            </label>
          );
        })}
      </fieldset>
      {error ? (
        <p className="text-sm text-danger-fg" role="alert" data-testid="demo-change-error">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => onApply(selected)}
          disabled={busy || selected === current}
          aria-busy={busy || undefined}
          data-testid="demo-change-apply"
        >
          {busy ? "Setting up the change…" : "Use this change"}
        </Button>
      </div>
    </div>
  );
}
